import React, { useState, useEffect } from 'react';
import { ToastProvider } from './hooks/useToast';
import { AuthProvider } from './hooks/useAuth';
import { DashboardLayout } from './layouts/DashboardLayout';
import { DashboardOverview } from './pages/DashboardOverview';
import { LinksList } from './pages/LinksList';
import { LinkEditor } from './pages/LinkEditor';
import { CampaignsList } from './pages/CampaignsList';
import { CampaignDetail } from './pages/CampaignDetail';
import { AnalyticsPage } from './pages/AnalyticsPage';
import { SettingsPage } from './pages/SettingsPage';
import { PublicLanding } from './pages/PublicLanding';

export const AppContent: React.FC = () => {
  const [currentPath, setCurrentPath] = useState<string>(() => {
    return window.location.pathname || '/';
  });

  // Listen to browser navigation (back/forward)
  useEffect(() => {
    const handlePopState = () => {
      setCurrentPath(window.location.pathname || '/');
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const navigate = (path: string) => {
    if (window.location.pathname !== path) {
      window.history.pushState({}, '', path);
      setCurrentPath(path);
      window.scrollTo(0, 0);
    }
  };

  // If visitor is at the root landing page
  if (currentPath === '/' || currentPath === '') {
    return <PublicLanding onEnterDashboard={() => navigate('/admin')} />;
  }

  // Determine current active view inside administrative dashboard
  const renderDashboardView = () => {
    // 1. /admin/links/new
    if (currentPath === '/admin/links/new') {
      return <LinkEditor onNavigate={navigate} />;
    }

    // 2. /admin/links/:id
    if (currentPath.startsWith('/admin/links/') && currentPath !== '/admin/links') {
      const linkId = currentPath.replace('/admin/links/', '');
      return <LinkEditor linkId={linkId} onNavigate={navigate} />;
    }

    // 3. /admin/links
    if (currentPath === '/admin/links') {
      return (
        <LinksList
          onNavigate={navigate}
          onOpenCreate={() => navigate('/admin/links/new')}
          onEditLink={(id) => navigate(`/admin/links/${id}`)}
        />
      );
    }

    // 4. /admin/campaigns/:id
    if (currentPath.startsWith('/admin/campaigns/') && currentPath !== '/admin/campaigns') {
      const campaignId = currentPath.replace('/admin/campaigns/', '');
      return (
        <CampaignDetail
          campaignId={campaignId}
          onNavigate={navigate}
          onOpenCreate={() => navigate('/admin/links/new')}
          onEditLink={(id) => navigate(`/admin/links/${id}`)}
        />
      );
    }

    // 5. /admin/campaigns
    if (currentPath === '/admin/campaigns') {
      return (
        <CampaignsList
          onNavigate={navigate}
          onSelectCampaign={(id) => navigate(`/admin/campaigns/${id}`)}
        />
      );
    }

    // 6. /admin/analytics
    if (currentPath === '/admin/analytics') {
      return <AnalyticsPage />;
    }

    // 7. /admin/settings
    if (currentPath === '/admin/settings') {
      return <SettingsPage />;
    }

    // Default /admin -> Overview
    return (
      <DashboardOverview
        onNavigate={navigate}
        onOpenCreate={() => navigate('/admin/links/new')}
      />
    );
  };

  return (
    <DashboardLayout activePath={currentPath} onNavigate={navigate}>
      {renderDashboardView()}
    </DashboardLayout>
  );
};

export const App: React.FC = () => {
  return (
    <ToastProvider>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ToastProvider>
  );
};

export default App;
